import { DirectionalHint, TooltipHost } from "@fluentui/react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
    Breadcrumb,
    BreadcrumbItem,
    ensure,
    RenderFunction,
    BucketContentExplorer,
    useDisclosure,
    ElevatedStack
} from "sprint-common-web";
import {
    BucketFile,
    BucketObject,
    isObjectFile,
    isObjectFolder
} from "sprint-common-web/src/components/explorer/BucketObject";

import { mapToBucketObject } from "../../../common/api/delivery-api/BucketInfoResponse";
import { GetArchiveBucketFilesQuery } from "../../../common/api/delivery-api/GetArchiveBucketFilesQuery";
import { SearchBucketArchiveQuery } from "../../../common/api/delivery-api/SearchBucketArchiveQuery";
import { useDeliveryApi } from "../../../common/api/delivery-api/useDeliveryApi";
import { OriginatorsDataProvider } from "../../../common/data/lookup-data-providers/OriginatorsDataProvider";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { ProcessBucketFilesDialog } from "../../common/ProcessBucketFilesDialog";
import { renderSelectedFilesTooltipContent } from "../../common/renderSelectedFilesTooltip";
import { DeliveryInfoForm, DeliveryInfoFormInputs, mapFormInputToRequest } from "../../delivery-info/DeliveryInfoForm";

export const BucketArchiveFilesDashboard: React.VFC = () => {
    const {
        getArchiveBucketFiles,
        abortGetArchiveBucketFilesRequest,
        createDelivery,
        searchArchiveBucket
    } = useDeliveryApi();
    const { archiveBucketName } = useParams<{ archiveBucketName: string }>() || "";
    const [selectedFiles, setSelectedFiles] = useState<BucketFile[]>([]);
    const [deliveryInfoFormInputs, setDeliveryInfoFormInputs] = useState<DeliveryInfoFormInputs>();
    const { isOpen: isDeliveryFormOpen, onOpen: openDeliveryForm, onClose: closeDeliveryForm } = useDisclosure();
    const { isOpen: isDeliveryModalOpen, onOpen: openDeliveryModal, onClose: closeDeliveryModal } = useDisclosure();

    const handleSearchBucketArchiveBucket = async (query: SearchBucketArchiveQuery): Promise<BucketObject[]> => {
        /* if search is performed when `getArchiveBucketFiles()` request is still pending, 
            then displayed search results would be overridden when this request completed */
        abortGetArchiveBucketFilesRequest();

        const response = await searchArchiveBucket(query);

        return (response.content ?? []).map(mapToBucketObject);
    };

    const handleOpenFolder = async (query: GetArchiveBucketFilesQuery): Promise<BucketObject[]> => {
        const response = await getArchiveBucketFiles(query);

        return (response?.content ?? []).map(mapToBucketObject);
    };

    const handleFormSubmit = async (inputs: DeliveryInfoFormInputs): Promise<void> => {
        setDeliveryInfoFormInputs(inputs);
        openDeliveryModal();
    };

    const handleObjectsSelection = (selectedObjects: BucketObject[]) => {
        if (selectedObjects.some(isObjectFolder)) {
            throw new Error("Folder should not be selected.");
        }

        setSelectedFiles(selectedObjects.filter(isObjectFile));
        openDeliveryForm();
    };

    const handleObjectSelection = (selectedObject: BucketObject) => {
        handleObjectsSelection([selectedObject]);
    };

    const handleProcessBucketFile = async (file: BucketFile) => {
        ensure(deliveryInfoFormInputs);
        await createDelivery(mapFormInputToRequest(deliveryInfoFormInputs, file.path, archiveBucketName));
    };

    const handleProcessingComplete = () => {
        closeDeliveryModal();
        closeDeliveryForm();
        setDeliveryInfoFormInputs(undefined);
    };

    const items: BreadcrumbItem[] = [
        {
            key: "content-ingestion",
            text: "Content Ingestions"
        },
        {
            key: "bucket-archive",
            text: "Archive Buckets",
            onClick: isDeliveryFormOpen
                ? () => {
                      closeDeliveryForm();
                  }
                : undefined
        }
    ];

    if (isDeliveryFormOpen) {
        items.push({
            key: "selected-files",
            text: selectedFiles.length === 1 ? selectedFiles[0].name : `${selectedFiles.length} files selected`,
            tooltipContent: selectedFiles.length > 1 ? renderSelectedFilesTooltipContent(selectedFiles) : undefined
        });
    }

    return (
        <OriginatorsDataProvider>
            <PageHeader>
                <Breadcrumb items={items} onRenderItem={renderBreadcrumb} />
            </PageHeader>
            {isDeliveryFormOpen ? (
                <ElevatedStack>
                    <DeliveryInfoForm onCancel={closeDeliveryForm} onSubmit={handleFormSubmit} horizontal={true} />
                </ElevatedStack>
            ) : (
                <BucketContentExplorer
                    selection={{
                        selectMany: {
                            text: "Upload files",
                            icon: "BulkUpload",
                            onClick: handleObjectsSelection
                        }
                    }}
                    bucketUniqueId={archiveBucketName}
                    onFolderClick={handleOpenFolder}
                    onFileClick={handleObjectSelection}
                    onSearch={handleSearchBucketArchiveBucket}
                    bucketName={archiveBucketName}
                />
            )}
            {isDeliveryModalOpen && (
                <ProcessBucketFilesDialog
                    title="Upload to Sprint"
                    message={
                        "You are about to upload files listed below to Sprint. Files will be removed from bucket and will appear on ingest list after small delay."
                    }
                    selectedFiles={selectedFiles}
                    onProcessFile={handleProcessBucketFile}
                    onProcessingComplete={handleProcessingComplete}
                    autoConfirm={true}
                />
            )}
        </OriginatorsDataProvider>
    );
};

const renderBreadcrumb = (item?: BreadcrumbItem, defaultRender?: RenderFunction<BreadcrumbItem>) => {
    ensure(item);
    ensure(defaultRender);

    return (
        <TooltipHost content={item.tooltipContent} directionalHint={DirectionalHint.rightBottomEdge}>
            {defaultRender({ ...item }, defaultRender)}
        </TooltipHost>
    );
};
